import Cookies from 'cookies';
import { type GetServerSideProps, type Redirect } from 'next';
import ErrorBoundary from '@/srp/ErrorBoundary';
import Search, { SearchData } from '@/srp/Search';
import SearchProviders from '@/srp/SearchProviders';
import HeadTags from '@/srp/SEO/HeadTags';
import WarmVDPCache from '@/srp/WarmVDPCache';
import setDatadogUser from '~/server-props/common/setDatadogUser';
import enableTestErrorPage from '~/server-props/srp/enableTestErrorPage';
import getProps, { type ResultProps } from '~/server-props/srp/getProps';
import getRedirects from '~/server-props/srp/getRedirects';
import getRequestContext from '~/server-props/srp/getRequestContext';

export default (props: ResultProps) => (
  <ErrorBoundary>
    <SearchProviders {...props.forProviders}>
      <HeadTags />
      <SearchData fetchedInventoryOnServer={props.fetchedInventoryOnServer} />
      <Search initialSeoProps={props.forSeo} />
      <WarmVDPCache />
    </SearchProviders>
  </ErrorBoundary>
);

export const getServerSideProps: GetServerSideProps<
  ResultProps
> = async context => {
  // Sometimes *.js.map files are sent to SRP. Throw them out ASAP.
  if (context.resolvedUrl.includes('.js.map')) {
    return { notFound: true };
  }

  const serverCookies = new Cookies(context.req, context.res, { secure: true });
  const requestContext = getRequestContext(context.req);

  const redirect = getRedirects(context) ?? {
    destination: '',
    permanent: false
  };

  if (redirect.destination) {
    return { redirect };
  }

  enableTestErrorPage(context);
  setDatadogUser(context, serverCookies);

  function setRedirect(r: Redirect) {
    Object.keys(redirect).forEach(key => {
      redirect[key] = r[key];
    });
  }

  const result = await getProps(
    context,
    requestContext,
    serverCookies,
    setRedirect
  );

  return redirect.destination ? { redirect } : result;
};
